import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalAdminStatus } from '@/hooks/useGlobalAdminStatus';
import { BadgeCheck, Building2, Clock, Loader2, Server } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/common/SearchableSelect';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type Customer = {
  organization_id: string;
  name: string;
  plan: 'trial' | 'paid';
  trial_state: 'active' | 'ending_soon' | 'expired' | null;
  days_left: number | null;
  cloud_servers: number;
  cloud_server_limit: number;
  legal_company_name: string;
  billing_contact_email: string;
  billing_contact_phone: string;
  tax_id: string;
  tax_id_vies_status: string;
  billing_country: string;
  invoicing_status: 'incomplete' | 'terms_pending' | 'awaiting_approval' | 'approved';
  invoice_terms_accepted_at: string | null;
  invoice_terms_accepted_by: string;
  approved_for_invoicing_at: string | null;
  approved_for_invoicing_by: string;
  pricing_tier: string;
  pricing_tier_name: string;
  enterprise_plan: string | null;
  enterprise_plan_name: string;
  license_expired_at: string | null;
  license_expired_by: string;
  license_expired_reason: string;
};

type TierOption = { slug: string; name: string; blurb: string };
type PlanOption = { id: string; name: string; slug: string; is_active: boolean };

type Payload = {
  customers: Customer[];
  counts: { awaiting_approval: number; approved: number; on_trial: number; license_expired: number };
  tiers: TierOption[];
  plans: PlanOption[];
};

const STATUS: Record<Customer['invoicing_status'], { label: string; tone: string }> = {
  awaiting_approval: { label: 'Awaiting approval', tone: 'bg-amber-500/15 text-amber-800' },
  approved: { label: 'Invoice billing', tone: 'bg-emerald-500/15 text-emerald-700' },
  terms_pending: { label: 'Has not agreed yet', tone: 'bg-muted text-muted-foreground' },
  incomplete: { label: 'No billing details', tone: 'bg-muted text-muted-foreground' },
};

function when(value: string | null) {
  return value ? new Date(value).toLocaleDateString() : '';
}

function planLabel(row: Customer) {
  if (row.plan === 'paid') return 'Paid';
  if (row.trial_state === 'expired') return 'Trial ended';
  if (row.days_left != null) return `Trial, ${row.days_left} day${row.days_left === 1 ? '' : 's'} left`;
  return 'Trial';
}

function TierEditor({ row, tiers, plans }: { row: Customer; tiers: TierOption[]; plans: PlanOption[] }) {
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: async (patch: Record<string, string | null>) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/customers/${row.organization_id}/`, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-admin-customers'] });
      toast.success(`${row.name} moved`);
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  return (
    <div className="space-y-1.5">
      <SearchableSelect
        className="h-8 w-48"
        value={row.pricing_tier}
        onChange={(tier) => save.mutate({ pricing_tier: tier })}
        placeholder="Tier"
        searchPlaceholder="Find a tier…"
        options={tiers.map((tier) => ({ value: tier.slug, label: tier.name, hint: tier.blurb ? undefined : undefined }))}
        testId={`customer-tier-${row.organization_id}`}
      />
      {row.pricing_tier === 'enterprise' && (
        <SearchableSelect
          className="h-8 w-48"
          value={row.enterprise_plan ?? ''}
          onChange={(plan) => save.mutate({ enterprise_plan: plan || null })}
          placeholder="No plan yet"
          searchPlaceholder="Find a plan…"
          options={[
            { value: '', label: 'No plan yet' },
            ...plans.filter((plan) => plan.is_active || plan.id === row.enterprise_plan)
              .map((plan) => ({ value: plan.id, label: plan.name })),
          ]}
          testId={`customer-plan-${row.organization_id}`}
        />
      )}
    </div>
  );
}

function LicenceSwitch({ row }: { row: Customer }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [confirmName, setConfirmName] = React.useState('');
  const [reason, setReason] = React.useState('');

  const change = useMutation({
    mutationFn: async (expired: boolean) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/customers/${row.organization_id}/license/`, {
        expired, confirm_name: confirmName, reason,
      });
    },
    onSuccess: (_data, expired) => {
      queryClient.invalidateQueries({ queryKey: ['global-admin-customers'] });
      setOpen(false); setConfirmName(''); setReason('');
      toast.success(expired ? `${row.name}'s licence is marked expired` : `${row.name}'s licence is in good standing`);
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  if (row.license_expired_at) {
    return (
      <div className="space-y-1">
        <Badge className="bg-red-500/15 text-red-700" data-testid={`customer-licence-expired-${row.organization_id}`}>Licence expired</Badge>
        <div className="text-xs text-muted-foreground">{when(row.license_expired_at)} by {row.license_expired_by || '—'}</div>
        {row.license_expired_reason && <div className="text-xs text-muted-foreground">{row.license_expired_reason}</div>}
        <Button size="sm" variant="ghost" onClick={() => change.mutate(false)} disabled={change.isPending} data-testid={`customer-licence-restore-${row.organization_id}`}>
          Put back in good standing
        </Button>
      </div>
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setOpen(true)} data-testid={`customer-licence-expire-${row.organization_id}`}>
        Mark licence expired
      </Button>
    );
  }

  return (
    <div className="space-y-1.5 rounded-md border p-2">
      <p className="text-xs text-muted-foreground">
        Their servers keep running. Every Bailey they run will say the licence has
        expired. Type <strong>{row.name}</strong> to confirm.
      </p>
      <Input className="h-8" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder="Organisation name" data-testid={`customer-licence-confirm-${row.organization_id}`} />
      <Input className="h-8" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (they will see it)" data-testid={`customer-licence-reason-${row.organization_id}`} />
      <div className="flex gap-1.5">
        <Button size="sm" variant="destructive" onClick={() => change.mutate(true)} disabled={change.isPending || confirmName !== row.name} data-testid={`customer-licence-save-${row.organization_id}`}>
          Mark expired
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setConfirmName(''); }}>Cancel</Button>
      </div>
    </div>
  );
}

function LimitEditor({ row }: { row: Customer }) {
  const queryClient = useQueryClient();
  const [value, setValue] = React.useState(String(row.cloud_server_limit));
  React.useEffect(() => setValue(String(row.cloud_server_limit)), [row.cloud_server_limit]);

  const save = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/customers/${row.organization_id}/`, { cloud_server_limit: value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-admin-customers'] });
      toast.success(`${row.name} may now run ${value} cloud server${value === '1' ? '' : 's'}`);
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const changed = value !== String(row.cloud_server_limit);
  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className="text-muted-foreground">{row.cloud_servers} of</span>
      <Input
        type="number"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-8 w-16 text-right"
        data-testid={`customer-limit-${row.organization_id}`}
      />
      {changed && (
        <Button size="sm" variant="outline" className="h-8" onClick={() => save.mutate()} disabled={save.isPending} data-testid={`customer-limit-save-${row.organization_id}`}>
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      )}
    </div>
  );
}

export default function CustomersPage() {
  const queryClient = useQueryClient();
  const { isGlobalAdmin } = useGlobalAdminStatus();

  const customers = useQuery({
    queryKey: ['global-admin-customers'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/customers/')).data as Payload;
    },
  });

  const approve = useMutation({
    mutationFn: async (row: Customer) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/customers/${row.organization_id}/approve/`);
    },
    onSuccess: (_, row) => {
      queryClient.invalidateQueries({ queryKey: ['global-admin-customers'] });
      toast.success(`${row.name} is approved for invoice billing and has been emailed`);
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const rows = customers.data?.customers ?? [];
  const counts = customers.data?.counts;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Customers</h2>
        <p className="text-muted-foreground">
          Every organisation on the platform: where they are in the trial, whether they have agreed to be
          invoiced, and how many cloud servers they may run. Open one for its billing details, servers, invoices
          and every email it was sent. Approval switches a company from the free trial to monthly invoices; the
          limit starts at three.
        </p>
      </div>

      {counts && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Card><CardContent className="pt-5">
            <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Awaiting approval</p>
            <p className="mt-1 text-3xl font-bold tracking-tight" data-testid="customers-awaiting">{counts.awaiting_approval}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><BadgeCheck className="h-3.5 w-3.5" /> On invoice billing</p>
            <p className="mt-1 text-3xl font-bold tracking-tight">{counts.approved}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Building2 className="h-3.5 w-3.5" /> On a free trial</p>
            <p className="mt-1 text-3xl font-bold tracking-tight">{counts.on_trial}</p>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All organisations</CardTitle>
          <CardDescription>Those waiting for approval are listed first.</CardDescription>
        </CardHeader>
        <CardContent>
          {customers.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No organisations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1">Organisation</th>
                    <th className="py-1">Plan</th>
                    <th className="py-1">Tier</th>
                    <th className="py-1">Billing</th>
                    <th className="py-1">Invoice billing</th>
                    <th className="py-1 text-right"><span className="inline-flex items-center gap-1"><Server className="h-3 w-3" /> Cloud servers</span></th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const state = STATUS[row.invoicing_status];
                    return (
                      <tr key={row.organization_id} className="border-t align-top" data-testid={`customer-${row.organization_id}`}>
                        <td className="py-2.5">
                          <Link to={`/customers/${row.organization_id}`} className="font-medium hover:underline" data-testid={`customer-open-${row.organization_id}`}>{row.name}</Link>
                          {row.legal_company_name && row.legal_company_name !== row.name && (
                            <div className="text-xs text-muted-foreground">{row.legal_company_name}</div>
                          )}
                        </td>
                        <td className="py-2.5">{planLabel(row)}</td>
                        <td className="py-2.5">
                          {isGlobalAdmin ? (
                            <TierEditor row={row} tiers={customers.data?.tiers ?? []} plans={customers.data?.plans ?? []} />
                          ) : (
                            <div>{row.pricing_tier_name}{row.enterprise_plan_name ? ` · ${row.enterprise_plan_name}` : ''}</div>
                          )}
                          {isGlobalAdmin ? <LicenceSwitch row={row} /> : row.license_expired_at && (
                            <Badge className="bg-red-500/15 text-red-700">Licence expired</Badge>
                          )}
                        </td>
                        <td className="py-2.5 text-xs text-muted-foreground">
                          {row.billing_contact_email ? (
                            <>
                              <div>{row.billing_contact_email}</div>
                              <div>{row.billing_contact_phone}</div>
                              {row.tax_id && <div>{row.tax_id}{row.tax_id_vies_status ? ` · VIES ${row.tax_id_vies_status}` : ''}</div>}
                              {row.billing_country && <div>{row.billing_country}</div>}
                            </>
                          ) : '—'}
                        </td>
                        <td className="py-2.5">
                          <Badge className={state.tone} data-testid={`customer-status-${row.organization_id}`}>{state.label}</Badge>
                          {row.invoicing_status === 'awaiting_approval' && (
                            <div className="mt-1 text-xs text-muted-foreground">agreed {when(row.invoice_terms_accepted_at)} by {row.invoice_terms_accepted_by || 'an admin'}</div>
                          )}
                          {row.invoicing_status === 'approved' && (
                            <div className="mt-1 text-xs text-muted-foreground">approved {when(row.approved_for_invoicing_at)} by {row.approved_for_invoicing_by || '—'}</div>
                          )}
                        </td>
                        <td className="py-2.5 text-right">{isGlobalAdmin ? <LimitEditor row={row} /> : <span className="text-muted-foreground">{row.cloud_servers} of {row.cloud_server_limit}</span>}</td>
                        <td className="py-2.5 text-right">
                          {isGlobalAdmin && row.invoicing_status === 'awaiting_approval' && (
                            <Button size="sm" onClick={() => approve.mutate(row)} disabled={approve.isPending} data-testid={`customer-approve-${row.organization_id}`}>
                              {approve.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <BadgeCheck className="mr-1 h-3.5 w-3.5" />}
                              Approve
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
